import React from "react";
import { useTranslation } from "react-i18next";
import { Kanban } from "@planzone/dto";
import { useMoveKanbanTask, useRetrieveKanban } from "@planzone/view-models";
import { Loader } from "@common/loader";
import { Filter } from "@common/filter";
import { DragAndDrop, DropBox } from "@common/dragAndDrop";
import { Task } from "./tasks/task";
import { Props } from "./types";
import { Tasks } from "./tasks";
import styles from "./styles.scss";

function sortKanban(kanban: Kanban): Kanban {
  return {
    ...kanban,
    columns: kanban.columns.map((column) => ({
      ...column,
      tasks: column.tasks.sort((current, next) =>
        current.statusKeyRank < next.statusKeyRank ? -1 : 1,
      ),
    })),
  };
}

function Statuses({ project, filter }: Props) {
  const { moveKanbanTask } = useMoveKanbanTask();
  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    kanban,
    retrieveKanban,
  } = useRetrieveKanban(project);
  const sortedKanban = sortKanban(kanban);
  const filteredColumns = sortedKanban.columns;
  const hasTasks =
    filteredColumns.reduce((total, { tasks }) => total + tasks.length, 0) > 0;
  const { t } = useTranslation();

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveKanban(project);
  }, [
    project,
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    retrieveKanban,
  ]);

  return isRequestPending ? (
    <div className={styles.loader}>
      <div className={styles.loaderMain}>
        <Loader />
      </div>
    </div>
  ) : (
    <DragAndDrop>
      <div className={styles.statuses}>
        {hasTasks ? (
          filteredColumns.map((column) => (
            <div key={column.status.uuid} className={styles.status}>
              <h3
                className={styles.name}
                style={{ backgroundColor: column.status.color }}
              >
                {t(`taskStatuses.${column.status.label}`, column.status.label)}
              </h3>
              <Filter
                data={column.tasks}
                filter={filter}
                extractCriteria={(task) => ({ task })}
              >
                {(tasks) => (
                  <div className={styles.tasks}>
                    {tasks.length > 0 ? (
                      <Tasks column={column} tasks={tasks} project={project} />
                    ) : (
                      <DropBox
                        acceptedTypes={["MOVE_TASK"]}
                        drop={({ payload }) =>
                          moveKanbanTask({
                            currentColumn: payload.column,
                            nextColumn: column,
                            task: payload.task,
                            range: {
                              min: null,
                              max: null,
                            },
                          })
                        }
                      >
                        {(data, canDrop) =>
                          !canDrop ? (
                            <div className={styles.placeholder}>
                              <p>
                                {t("projectPage.listsPage.statuses.emptyList")}
                              </p>
                            </div>
                          ) : (
                            <div className={styles.dropPreview}>
                              {canDrop && (
                                <Task
                                  task={data.payload.task}
                                  project={project}
                                />
                              )}
                            </div>
                          )
                        }
                      </DropBox>
                    )}
                  </div>
                )}
              </Filter>
            </div>
          ))
        ) : (
          <h3 className={styles.noTasksFound}>
            {t("taskRetrieval.notifications.noTasksFound")}
          </h3>
        )}
      </div>
    </DragAndDrop>
  );
}

export { Statuses };
